import builder from "@rsql/builder";
import { emit } from "@rsql/emitter";
import {
	ColDef,
	DateFilterModel,
	ICombinedSimpleModel,
	ISimpleFilterModel,
	NumberFilterModel,
	SetFilterModel,
	TextFilterModel,
} from "ag-grid-community";
import { IS_NOT_NULL, IS_NULL } from "../Rsql/ast";

type GridFilterType =
	| "equals"
	| "notEqual"
	| "contains"
	| "notContains"
	| "startsWith"
	| "endsWith"
	| "lessThan"
	| "lessThanOrEqual"
	| "greaterThan"
	| "greaterThanOrEqual"
	| "inRange"
	| "blank"
	| "notBlank"
	| "empty"
	| "in"
	| "notIn"
	| "simpleSearch"
	| "advancedSearch";

/**
 * Assertions
 */
export function isCombinedFilter(filter: ISimpleFilterModel): filter is ICombinedSimpleModel<ISimpleFilterModel> {
	return "operator" in filter && (filter.operator === "AND" || filter.operator === "OR");
}

export function isTextFilter(filter: ISimpleFilterModel): filter is TextFilterModel {
	return filter.filterType === "text";
}

export function isNumberFilter(filter: ISimpleFilterModel): filter is NumberFilterModel {
	return filter.filterType === "number";
}

export function isDateFilter(filter: ISimpleFilterModel): filter is DateFilterModel {
	return filter.filterType === "date";
}

export function isSetFilter(filter: ISimpleFilterModel): filter is SetFilterModel {
	return filter.filterType === "set";
}

/**
 * Convert an ag-grid filter model to an RSQL string.
 */
export function convertFilterModelToRSQLString(
	filterModel: Record<string, ICombinedSimpleModel<ISimpleFilterModel> | ISimpleFilterModel>,
	columnDefs: ColDef[],
) {
	const comparisonNodes = Object.entries(filterModel).map(([key, fm]) => {
		const colDef = columnDefs.find((c) => c.field == key) as ColDef;
		if (isCombinedFilter(fm) && fm.conditions?.length) {
			if (fm.operator === "OR") {
				return builder.or(
					...fm.conditions.map((condition) => {
						return filterModelToComparisonNode(key, condition, colDef);
					}),
				);
			} else if (fm.operator === "AND") {
				return builder.and(
					...fm.conditions.map((condition) => {
						return filterModelToComparisonNode(key, condition, colDef);
					}),
				);
			}
		}

		return filterModelToComparisonNode(key, fm, colDef);
	});

	if (comparisonNodes.length > 0) {
		return emit(builder.and(...comparisonNodes));
	}

	return "";
}

/**
 *
 */
export function filterModelToComparisonNode(filterKey: string, filter: ISimpleFilterModel, colDef: ColDef) {
	if (isTextFilter(filter)) {
		return getComparisonNode(
			filterKey,
			// TODO: Fix TS error
			// @ts-ignore-next-line
			[filter.filter, filter.filterTo],
			filter.type,
		);
	} else if (isNumberFilter(filter)) {
		return getComparisonNode(
			filterKey,
			// TODO: Fix TS error
			// @ts-ignore-next-line
			[filter.filter, filter.filterTo],
			filter.type,
		);
	} else if (isDateFilter(filter)) {
		return getComparisonNode(
			filterKey,
			// TODO: Fix TS error
			// @ts-ignore-next-line
			[filter.dateFrom, filter.dateTo],
			filter.type,
		);
	} else if (isSetFilter(filter)) {
		if ("collection" in colDef.filterParams) {
			return builder.or(...filter.values.map((value) => builder.comparison(filterKey, "=contains=", value as string)));
		}

		// One value and it's falsy
		if (filter.values.length === 1 && !filter.values[0]) {
			return builder.comparison(filterKey, IS_NULL, "");
		}

		// Multiple values and one of them is null
		if (filter.values.includes(null)) {
			return builder.or(
				builder.comparison(filterKey, IS_NULL, ""),
				builder.in(filterKey, filter.values.filter((v) => !!v).map(String)),
			);
		}

		// No null values
		return getComparisonNode(filterKey, filter.values.map(String), "in");
	} else {
		throw new Error(`Unknown filter type: '${filter.filterType}' provided!`);
	}
}

export function getComparisonNode(key: string, filter: (string | number)[], filterType: GridFilterType) {
	switch (filterType) {
		case "equals":
			return builder.eq(key, filter[0]);
		case "notEqual":
			return builder.neq(key, filter[0]);
		case "contains":
			return builder.eq(key, `*${filter[0]}*`);
		case "notContains":
			return builder.neq(key, `*${filter[0]}*`);
		case "startsWith":
			return builder.eq(key, `${filter[0]}*`);
		case "endsWith":
			return builder.eq(key, `*${filter[0]}`);
		case "lessThan":
			return builder.lt(key, filter[0]);
		case "lessThanOrEqual":
			return builder.le(key, filter[0]);
		case "greaterThan":
			return builder.gt(key, filter[0]);
		case "greaterThanOrEqual":
			return builder.ge(key, filter[0]);
		case "inRange":
			return builder.logic([builder.ge(key, filter[0]), builder.le(key, filter[1])], "and");
		case "blank":
			return builder.comparison(key, IS_NULL, "");
		case "notBlank":
			return builder.comparison(key, IS_NOT_NULL, "");
		case "empty":
			return builder.eq(key, "");
		case "in":
			return builder.in(key, filter);
		case "simpleSearch": {
			const terms = (filter[0] as string)
				?.trim()
				.split(" ")
				.map((it) => builder.comparison(key, "==", `*${it}*`));

			return builder.and(...terms);
		}
		case "advancedSearch":
			return builder.comparison(key, "=search=", filter[0]);
		default:
			throw new Error("Invalid filter type provided!");
	}
}
